import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft, User, Calendar, Users } from 'lucide-react';
import Navigation from './Navigation';
import { useFetch } from '../hooks/useApi';
import { apiService } from '../services/apiService';
import { toast } from 'sonner';
import RunCard from './ui/RunCard';
import ClubViewButton from './ui/ClubViewButton';
import ScrollableCardModal from './ui/ScrollableCardModal';
import { useAuth } from '../contexts/AuthContext';

const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [connections, setConnections] = useState([]);
  const [showRunCardModal, setShowRunCardModal] = useState(false);

  // Check if viewing own profile
  const isOwnProfile = currentUser && currentUser.id === userId;

  // Fetch user profile using useFetch hook
  const { data: userProfile, loading, error } = useFetch(
    () => apiService.users.getProfile(userId),
    [userId],
    { enabled: !!userId }
  );

  // Only fetch connections if viewing own profile (getMyConnections requires auth)
  // For other users' profiles, we don't show their connections (privacy)
  const { data: userConnections, loading: connectionsLoading, error: connectionsError } = useFetch(
    () => apiService.clubs.getMyConnections(),
    [userId],
    { enabled: !!userId && isOwnProfile && user?.user_type === 'runner' }
  );

  // Update state when data changes
  useEffect(() => {
    if (userProfile) {
      setUser(userProfile);
    }
  }, [userProfile]);

  // Update connections when data changes
  useEffect(() => {
    if (userConnections) {
      setConnections(userConnections);
    }
  }, [userConnections]);

  // Handle connections errors gracefully (don't show error, just don't display connections)
  useEffect(() => {
    if (connectionsError && isOwnProfile) {
      // Silently handle error - connections section just won't show
      console.log('Could not load connections:', connectionsError);
    }
  }, [connectionsError, isOwnProfile]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error('Failed to load user profile');
      navigate(-1); // Go back to previous page
    }
  }, [error, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <Navigation />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-runcards-text-primary text-runcards-lg font-runcards">
            Loading user profile...
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <Navigation />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-runcards-text-primary text-runcards-lg mb-4 font-runcards">
              User profile not found
            </div>
            <Button onClick={handleBack} variant="outline" className="border-runcards-border/40 text-runcards-text-primary hover:bg-white/10">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <Button 
            onClick={handleBack} 
            variant="outline" 
            className="border-runcards-border/40 text-runcards-text-primary hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        {/* User Profile Content */}
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Run Card */}
            <div>
              <h2 className="text-runcards-xxl font-runcards-bold text-runcards-text-primary mb-6 font-runcards-bold">
                Run Card
              </h2>
              <RunCard 
                runner={user}
                showHover={false}
                buttonText="View Profile"
                onButtonClick={() => {
                  setShowRunCardModal(true);
                }}
              />
            </div>

            {/* User Info */}
            <div>
              <h2 className="text-runcards-xxl font-runcards-bold text-runcards-text-primary mb-6 font-runcards-bold">
                Runner Information
              </h2>
              
              <Card className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl">
                <CardHeader>
                  <CardTitle className="text-runcards-text-primary flex items-center font-runcards-bold">
                    <User className="w-5 h-5 mr-2" />
                    {user.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {user.created_at && (
                    <div className="flex items-center text-runcards-text-primary/80">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span className="font-runcards">
                        Connected since {new Date(user.created_at).toLocaleDateString('en-US', { 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Club Connections - Only show if viewing own profile */}
              {user.user_type === 'runner' && isOwnProfile && (
                <Card className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl mt-6">
                  <CardHeader>
                    <CardTitle className="text-runcards-text-primary flex items-center font-runcards-bold">
                      <Users className="w-5 h-5 mr-2" />
                      My Club Connections
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {connectionsLoading ? (
                      <div className="text-runcards-text-primary/60 font-runcards">
                        Loading connections...
                      </div>
                    ) : connections && connections.length > 0 ? (
                      <div className="space-y-3">
                        {connections.map((connection) => (
                          <div key={connection.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-runcards-border/10">
                            <div className="flex items-center flex-1">
                              {/* Club Image */}
                              {connection.club_image && (
                                <div className="w-10 h-10 rounded-lg overflow-hidden mr-3 flex-shrink-0">
                                  <img 
                                    src={connection.club_image} 
                                    alt={connection.club_name || 'Club'}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              
                              {/* Club Info */}
                              <div className="flex-1">
                                <div className="text-runcards-text-primary font-runcards-bold font-runcards">
                                  {connection.club_name || 'Club'}
                                </div>
                                <div className="text-runcards-text-primary/60 text-runcards-sm font-runcards">
                                  {connection.club_city} • {connection.club_type}
                                </div>
                                <div className="text-runcards-text-primary/50 text-runcards-xs font-runcards">
                                  Connected {new Date(connection.connected_at).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </div>
                              </div>
                            </div>
                            
                            {/* View Club Button */}
                            <ClubViewButton
                              clubId={connection.club_id}
                              variant="outline"
                              size="sm"
                              className="border-runcards-border/20 text-runcards-text-primary hover:bg-white/10 ml-3"
                            >
                              View Club
                            </ClubViewButton>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-runcards-text-primary/60 font-runcards">
                        No club connections yet
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Run Card Modal */}
      <ScrollableCardModal
        cardType="runner"
        cardId={user?.id}
        isOpen={showRunCardModal}
        onClose={() => setShowRunCardModal(false)}
      />
    </div>
  );
};

export default UserProfilePage;
